import { useState } from "react";
import type { Team, TeamMember } from "@/lib/api/types";
import { EntityCard } from "@/components/ui/entity-card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/lib/get-initials";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, Trash2 } from "lucide-react";
import { useDeleteTeam } from "@/hooks/queries/use-teams";
import { ApiError } from "@/lib/api/client";
import { getTeamHueColor, getTeamIconPath } from "@/lib/team-hue";
import { getTeamLevelBadgeClass, getTeamLevelLabel } from "@/lib/team-hierarchy";
import { Badge } from "@/components/ui/badge";

interface TeamCardProps {
  team: Team;
  members?: TeamMember[];
  memberCount?: number;
}

export function TeamCard({ team, members, memberCount }: TeamCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [error, setError] = useState("");
  const deleteTeam = useDeleteTeam();

  const handleDelete = async () => {
    setError("");
    try {
      await deleteTeam.mutateAsync(team.id);
      setShowDeleteDialog(false);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const data = err.data as { error?: { message?: string } } | undefined;
        setError(data?.error?.message || `Error ${err.response.status}: Failed to delete team`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to delete team");
      }
    }
  };

  const realMembers = members ?? [];
  const hasMemberData = memberCount != null || team._count?.memberships != null || members != null;
  const displayCount = memberCount ?? team._count?.memberships ?? realMembers.length;
  const maxVisible = 6;
  const hueColor = getTeamHueColor(team.name);
  const iconPath = getTeamIconPath(team.name);
  const peopleLabel = `${displayCount} member${displayCount !== 1 ? 's' : ''}`;
  const projectDesc = team.description ?? "";

  // Build avatar list from real members only
  const avatarElements: { key: string; url: string | null; name: string }[] = realMembers.map(m => ({
    key: m.id,
    url: m.user.avatarUrl ?? null,
    name: m.user.name,
  }));
  const visibleAvatars = avatarElements.slice(0, maxVisible);
  const overflow = displayCount - visibleAvatars.length;

  const memberFooter = (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="flex -space-x-2">
          {visibleAvatars.map((a) => (
            <Avatar key={a.key} className="h-8 w-8 border-2 border-white/[0.08]">
              {a.url && <AvatarImage src={a.url} alt={a.name} />}
              <AvatarFallback className="text-xs bg-white/[0.06] text-muted-foreground font-medium">
                {getInitials(a.name)}
              </AvatarFallback>
            </Avatar>
          ))}
          {overflow > 0 && visibleAvatars.length > 0 && (
            <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white/[0.08] bg-white/[0.06] text-xs font-medium text-muted-foreground">
              +{overflow}
            </div>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{peopleLabel}</span>
      </div>
      {team.canOwnEpics ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-emerald-300">
          <Sparkles className="h-3 w-3" />
          Epic owner
        </span>
      ) : null}
    </div>
  );

  return (
    <>
      <EntityCard
        href={`/teams/${team.id}`}
        hueColor={hueColor}
        icon={
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d={iconPath} stroke={hueColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        }
        title={team.name}
        subtitle={projectDesc}
        badge={
          <Badge
            variant="outline"
            className={`border px-2 py-0 text-[10px] font-medium ${getTeamLevelBadgeClass(team.level)}`}
          >
            {getTeamLevelLabel(team.level)}
          </Badge>
        }
        secondaryMeta={team.canOwnEpics ? "Can own epics" : team.childCount ? `${String(team.childCount)} child teams` : undefined}
        footer={hasMemberData ? memberFooter : undefined}
        menuItems={
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={(e) => { e.stopPropagation(); setShowDeleteDialog(true); }}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        }
      />

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{team.name}"? This will also
              delete all epics, features, and tasks in this team. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {error && <div className="text-sm text-destructive">{error}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={deleteTeam.isPending}>
              {deleteTeam.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
